import { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import Button from "../../components/Button/Button";
import CardCount from "../../components/CardCount/CardCount";
import {
  Group as StudentIcon,
  School as GraduateIcon,
  EmojiPeople as TeacherIcon,
  AddCircle,
  People as GuardianIcon,
  Class as ClassIcon,
  MenuBook as SubjectIcon,
  Assignment as LessonNotesIcon,
  Schedule as PeriodIcon,
  LibraryBooks as SyllabusIcon,
  Ballot as SyllabusTopicsIcon,
  EventAvailable as ExamSchedulesIcon,
  AssignmentTurnedIn as ExamsIcon,
  Grading as GradesIcon,
  TrendingUp as GradingSystemsIcon,
  Receipt as FeeInvoicesIcon,
  AttachMoney as FeeItemsIcon,
  Description as StudentInvoicesIcon,
  HowToReg as ApplicantsIcon,
  School as EnrolmentsIcon,
  Quiz as EnrolmentExamsIcon,
  DynamicForm as CustomFormsIcon,
  Category as CategoriesIcon,
  Inventory as ItemsIcon,
  Event as EventsIcon,
  EventSeat as SessionsIcon,
  DateRange as TermsIcon,
  Person as UserIcon
} from "@mui/icons-material";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import InputGroup from "../../components/FormComponent/InputGroup";
import Modal from "../../components/Modal/index";
import useStyles from "./styles";
import Table from "../../components/Table/Table";
import WidgetTitle from "./components/WidgetTitle/WidgetTitle";
import { Link } from "react-router-dom";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";

const OVERVIEW_ENDPOINT = "/admin/v1/overview/super-admin";
const LOGIN_ENDPOINT = "/admin/v1/users/login-activity";

const AdminDashboard = () => {
  const classes = useStyles();
  const [modalisopen, setModalIsOpen] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [analyticData, setAnalyticData] = useState({});
  const [loginData, setLoginData] = useState([]);

  const getOverview = () => {
    httprequest
      .get(OVERVIEW_ENDPOINT)
      .then((res) => {
        setOverviewData(res?.data?.data?.core);
        setAnalyticData(res?.data?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data || "Failed to fetch dashboard data");
      });
  };
  const handleGetRecentLogin = () => {
    httprequest.get(LOGIN_ENDPOINT).then((res) => {
      setLoginData(res?.data?.data?.data);
    }).catch((err) => {
      toast.error(err?.response?.data?.message);
    });
  };
  useEffect(() => {
    getOverview();
    handleGetRecentLogin();
  }, []);

  const recentSchoolsColumns = [
    {
      name: "School Name",
      selector: (row) => row?.school_name
    },
    {
      name: "Email",
      selector: (row) => row?.email
    },
    {
      name: "Country",
      selector: (row) => row?.country
    },
    {
      name: "Students",
      selector: (row) => row?.total_students
    },
    {
      name: "Teachers",
      selector: (row) => row?.total_teachers
    },
    {
      name: "Created At",
      selector: (row) => row?.created_at
    }
  ];

  const topUsersColumns = [
    { name: "Full Name", selector: (row) => row?.full_name },
    { name: "Email", selector: (row) => row?.email },
    { name: "Active Users", selector: (row) => row?.active_users_count }
  ];

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return { browser: "Unknown", device: "Unknown" };

    let browser = "Unknown";
    let device = "Unknown";

    if (userAgent.includes("Postman")) {
      browser = "Postman";
      device = "API Client";
    } else if (userAgent.includes("Chrome")) {
      browser = "Chrome";
    } else if (userAgent.includes("Firefox")) {
      browser = "Firefox";
    } else if (userAgent.includes("Safari")) {
      browser = "Safari";
    }

    if (userAgent.includes("Android")) device = "Android";
    else if (userAgent.includes("Windows")) device = "Windows";
    else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) device = "iOS";
    else if (userAgent.includes("Mac")) device = "MacOS";
    else if (userAgent.includes("Linux")) device = "Linux";

    return { browser, device };
  };

  const recentUser = [
    {
      name: "Device",
      selector: (row) => parseUserAgent(row.user_agent).device,
      sortable: true
    },
    {
      name: "Location",
      selector: (row) => row.location || "Unknown"
    },
    {
      name: "IP Address",
      selector: (row) => row.ip_address
    },
    {
      name: "Browser",
      selector: (row) => parseUserAgent(row.user_agent).browser
    },
    {
      name: "Time",
      selector: (row) => new Date(row.login_at).toLocaleString(), // Formats the ISO date
      sortable: true
    }
  ];

  return (
    <Layout
      children={
        <>
          <PageTitle
            title="Dashboard"
            subtitle="See the overview of everything going on at your organisation."
            button={
              <Button
                variant="primary"
                buttonSize="small"
                color="btndefault"
                onClick={() => {
                  setModalIsOpen(true);
                }}
                buttonName="Create a class"
              />
            }
          />
          <WidgetTitle title="Core Metrics" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Schools"
                count={analyticData?.core?.total_schools || 0}
                icon={<GraduateIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Users (Incl. Lulu)"
                count={analyticData?.core?.total_users_including_lulu || 0}
                icon={<UserIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Users (Excl. Lulu)"
                count={analyticData?.core?.total_users_excluding_lulu || 0}
                icon={<UserIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Students"
                count={analyticData?.core?.total_students || 0}
                icon={<StudentIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Employees"
                count={analyticData?.core?.total_employees || 0}
                icon={<TeacherIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Total Guardians"
                count={analyticData?.core?.total_guardians || 0}
                icon={<GuardianIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>

          {/* Academics */}
          <WidgetTitle title="Academics" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Classes"
                count={analyticData?.academics?.classes || 0}
                icon={<ClassIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Subjects"
                count={analyticData?.academics?.subjects || 0}
                icon={<SubjectIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Lesson Notes"
                count={analyticData?.academics?.lesson_notes || 0}
                icon={<LessonNotesIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Periods"
                count={analyticData?.academics?.periods || 0}
                icon={<PeriodIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Syllabus"
                count={analyticData?.academics?.syllabus || 0}
                icon={<SyllabusIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Syllabus Topics"
                count={analyticData?.academics?.syllabus_topics || 0}
                icon={<SyllabusTopicsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>

          {/* Exams & Grading */}
          <WidgetTitle title="Exams & Grading" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Exam Schedules"
                count={analyticData?.exams_and_grading?.exam_schedules || 0}
                icon={<ExamSchedulesIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Exams"
                count={analyticData?.exams_and_grading?.exams || 0}
                icon={<ExamsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Grades"
                count={analyticData?.exams_and_grading?.grades || 0}
                icon={<GradesIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Grading Systems"
                count={analyticData?.exams_and_grading?.grading_systems || 0}
                icon={<GradingSystemsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>

          {/* Finance */}
          <WidgetTitle title="Finance" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Fee Invoices"
                count={analyticData?.finance?.fee_invoices || 0}
                icon={<FeeInvoicesIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Fee Items"
                count={analyticData?.finance?.fee_items || 0}
                icon={<FeeItemsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Student Invoices"
                count={analyticData?.finance?.student_invoices || 0}
                icon={<StudentInvoicesIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>

          {/* Admissions */}
          <WidgetTitle title="Admissions" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Applicants"
                count={analyticData?.admissions?.applicants || 0}
                icon={<ApplicantsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Enrolments"
                count={analyticData?.admissions?.enrolments || 0}
                icon={<EnrolmentsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Enrolment Exams"
                count={analyticData?.admissions?.enrolment_exams || 0}
                icon={<EnrolmentExamsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Custom Forms"
                count={analyticData?.admissions?.custom_forms || 0}
                icon={<CustomFormsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>

          {/* Inventory */}
          <WidgetTitle title="Inventory" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Categories"
                count={analyticData?.inventory?.categories || 0}
                icon={<CategoriesIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Items"
                count={analyticData?.inventory?.items || 0}
                icon={<ItemsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>

          {/* Events & Sessions */}
          <WidgetTitle title="Events & Sessions" />
          <Grid container spacing={4}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Events"
                count={analyticData?.events_and_sessions?.events || 0}
                icon={<EventsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Sessions"
                count={analyticData?.events_and_sessions?.sessions || 0}
                icon={<SessionsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <CardCount
                title="Terms"
                count={analyticData?.events_and_sessions?.terms || 0}
                icon={<TermsIcon />}
                isLoading={!analyticData}
              />
            </Grid>
          </Grid>
          <WidgetTitle
            title="Top Users"
            subtitle="Users with the highest number of active users"
          />
          <Table data={overviewData?.top_users || []} columns={topUsersColumns} />
          <WidgetTitle
            title="Recent Schools"
            subtitle="See the latest registered schools"
            link={<Link className={classes.widgetlink} to="/schools">See all schools</Link>}
          />
          <Table data={overviewData?.recent_schools || []} columns={recentSchoolsColumns} />
          <WidgetTitle
            title="Recent Login Activity"
          />
          <Table data={loginData} columns={recentUser} pagination paginationPerPage="5" />
          <Modal
            title="Add a class"
            subtitle="You can add a new class and assign a teacher to the class."
            modalContent={
              <>
                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Class Name"
                      margin="normal"
                      placeHolder="Enter Class Name"
                      inputType="text"
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      label="Class Teacher"
                      margin="normal"
                      placeHolder="Select Class Teacher"
                      inputType="text"
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <InputGroup
                      label="Subject"
                      margin="normal"
                      placeHolder="Subjects"
                      inputType="text"
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <InputGroup
                      label="Subject"
                      margin="normal"
                      placeHolder="Subjects"
                      inputType="text"
                    />
                  </Grid>
                </Grid>
                <div className={`${classes.extrasubject}`}>
                  <p className={classes.subject_add}>Add another Subject</p>
                  <AddCircle className={classes.addicon} />
                </div>
              </>
            }
            modalFooter={
              <>
                <Button variant="primary" buttonName="Add Class" buttonSize="full" color="btndefault" />
              </>
            }
            isopen={modalisopen}
            closeModal={() => {
              setModalIsOpen(false);
            }}
          />
        </>
      }
    >
    </Layout>
  );
};

export default AdminDashboard;
